import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { AuthProvider, AuthScreen, createAuthClient, loadAuthConfig } from '@nsi-advisory-services/auth-react';
import API_CONFIG, { buildApiUrl } from './config/apiConfig';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ThemeProvider from './theme/ThemeProvider';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'rsuite/dist/rsuite.min.css';
import '@nsi-advisory-services/components/dist/styles.css';
import './index.css';
import './theme/theme-dark.css';

/**
 * "ResizeObserver loop completed with undelivered notifications" means the
 * observer ran out of frame budget before every callback had been delivered, and
 * that the rest will follow on the next frame. It is defined as recoverable:
 * nothing is lost by it, and there is nothing to fix in the code that triggered
 * it. It reaches the page as a bare ErrorEvent on window, which no application
 * code can catch — and React's dev-only error overlay listens on window and
 * paints any ErrorEvent as a fatal crash screen. That overlay is the only reason
 * this is ever seen; production is unaffected either way.
 *
 * Two implementations raise it and each needs its own answer, below.
 */
const RESIZE_OBSERVER_NOTICES = [
    'ResizeObserver loop completed with undelivered notifications',
    'ResizeObserver loop limit exceeded',
];

/*
 * The browser's own. Deferring each callback by a frame stops it being raised at
 * all: it fires when a callback changes layout during delivery, and a callback
 * that runs after delivery has finished cannot. One frame of lag is invisible
 * for what this app observes with it — a table measuring its own rows.
 */
const NativeResizeObserver = window.ResizeObserver;
if (NativeResizeObserver) {
    window.ResizeObserver = class ResizeObserver extends NativeResizeObserver {
        constructor(callback) {
            super((entries, observer) => {
                window.requestAnimationFrame(() => callback(entries, observer));
            });
        }
    };
}

/*
 * The bundled one. The wrapper above only covers code that reaches for
 * window.ResizeObserver; RSuite doesn't — it imports @juggle/resize-observer
 * directly, so its pickers, overlays and modals run that implementation whatever
 * the global says. That one reports the same condition by building an ErrorEvent
 * by hand and calling
 * window.dispatchEvent (see @juggle/resize-observer/lib/algorithms/
 * deliverResizeLoopError.js), which no listener of ours can get in front of —
 * the dev overlay registers its handler from an earlier bundle entry, so it
 * always hears the event first.
 *
 * Refusing to dispatch the synthetic event is therefore the only place to stop
 * it. The match is on a manufactured 'error' event carrying one of two exact
 * messages; a real error, from anywhere, is dispatched by the browser itself and
 * never passes through here.
 */
const nativeDispatchEvent = window.dispatchEvent.bind(window);

window.dispatchEvent = (event) => {
    const message = String(event?.message || '');
    if (event?.type === 'error' && RESIZE_OBSERVER_NOTICES.some((notice) => message.includes(notice))) {
        return true;
    }
    return nativeDispatchEvent(event);
};

/**
 * Create QueryClient for React Query - best practice is to create it outside component tree
 */
const queryClient = new QueryClient();

const root = createRoot(document.getElementById('root'));

async function start() {
    try {
        const config = await loadAuthConfig(buildApiUrl('/auth/config'));
        const client = await createAuthClient(config, { redirectUri: API_CONFIG.FRONTEND_URL });
        root.render(
            <ThemeProvider>
                <QueryClientProvider client={queryClient}>
                    <AuthProvider client={client} config={config}>
                        <App />
                    </AuthProvider>
                </QueryClientProvider>
            </ThemeProvider>
        );
    } catch (error) {
        console.error('Sign-in setup failed:', error);
        root.render(<AuthScreen title="Can't reach EmployeeConnect" message="Refresh to try again." />);
    }
}

start();
